import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './AuthContext'
import { useWebSocket, type WsMessage } from '../hooks/useWebSocket'
import { getNotifications, type BellEvent } from '../lib/api'

// ── Notification model ───────────────────────────────────────────────────────
// The shape is deliberately storage-agnostic. Today these objects are built
// client-side from realtime hub events and cached in localStorage, but the exact
// same shape can later be served by a backend `/notifications` endpoint (read
// state, pagination, cross-device sync) without changing a single consumer —
// only the source in this provider would swap from "socket only" to "API + socket".
export type NotificationType = 'order' | 'system'

export type AppNotification = {
  id: string
  type: NotificationType
  title: string
  body?: string
  /** Optional in-app target. Reserved for a future "click to open" — not routed
   *  yet because the bell is shared across apps and a path valid in one app
   *  (e.g. Ping's /management/orders) would 404 in another. */
  href?: string
  /** ISO 8601 timestamp. */
  timestamp: string
  read: boolean
  /** The event it came from — what decides which app's bell shows it. */
  event?: string
}

/**
 * Which events each app's bell shows. The bell is shared by every app and the
 * socket and backfill carry the whole org's events, so without this the
 * Cashier back office and the till listed screen-offline alerts, licence
 * warnings and publish failures that belong to Frame. An app not listed here
 * (Frame, My) shows everything, as before.
 */
const APP_EVENTS: Record<string, ReadonlySet<string>> = {
  pos: new Set(['order_created', 'user_joined']),
  ping: new Set(['order_created', 'user_joined']),
}

/** Does this app's bell show this notification? */
function belongsTo(app: string | undefined, n: AppNotification): boolean {
  const allowed = app ? APP_EVENTS[app] : undefined
  if (!allowed) return true
  // Saved before notifications recorded their event: an order is the one kind
  // that can be told apart, so keep those and drop the rest.
  return n.event ? allowed.has(n.event) : n.type === 'order'
}

type NotificationValue = {
  notifications: AppNotification[]
  unreadCount: number
  markAllRead: () => void
  markRead: (id: string) => void
  clearAll: () => void
  /** Drop one notification — the swipe-away on a phone. */
  dismiss: (id: string) => void
}

const NotificationContext = createContext<NotificationValue | null>(null)

const MAX_ITEMS = 50
const STORAGE_PREFIX = 'wetook_notifications_v1'

const rid = () =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`

const storageKey = (userId: number) => `${STORAGE_PREFIX}_${userId}`
// What the user dismissed or cleared, so the server backfill doesn't bring it
// back: `clearedAt` hides everything up to that instant, `ids` single dismissals.
const hiddenKey = (userId: number) => `wetook_notifications_hidden_v1_${userId}`
type Hidden = { clearedAt?: string; ids: string[] }

function loadHidden(userId: number): Hidden {
  try {
    const parsed = JSON.parse(localStorage.getItem(hiddenKey(userId)) || '{}')
    return { clearedAt: parsed.clearedAt, ids: Array.isArray(parsed.ids) ? parsed.ids : [] }
  } catch {
    return { ids: [] }
  }
}

function saveHidden(userId: number, hidden: Hidden) {
  try {
    localStorage.setItem(hiddenKey(userId), JSON.stringify({ ...hidden, ids: hidden.ids.slice(-200) }))
  } catch {
    /* ignore */
  }
}

// Backfill at most this often (login, tab focus and tab-visible all trigger it).
const BACKFILL_MIN_INTERVAL_MS = 60_000
// A live entry without a stable id (e.g. a licence expiry) and its backfilled
// copy are the same event when title + body match within this window.
const SAME_EVENT_WINDOW_MS = 10 * 60_000

/** Server backfill item → the WS message it stands for, so one mapper serves both. */
function bellEventToMessage(e: BellEvent): WsMessage {
  if (e.kind === 'task_failed') {
    return { event: 'task_update', data: { task: { ...e.data, status: 'failed' } } } as WsMessage
  }
  return { event: e.kind, data: e.data } as WsMessage
}

function loadStored(userId: number): AppNotification[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as AppNotification[]) : []
  } catch {
    return []
  }
}

// ── Event → notification mapping ─────────────────────────────────────────────
// Turns a realtime hub event into a notification, or returns null to ignore it.
// To surface a new event in the bell, add a case here — persistence, the unread
// badge, and the panel UI all keep working unchanged.
type TFunc = ReturnType<typeof useTranslation>['t']

function toNotification(msg: WsMessage, t: TFunc): AppNotification | null {
  const base = toNotificationBody(msg, t)
  if (!base) return null
  // Attention events carry a stable id (the same one GET /notifications
  // backfills with) so a live entry and its backfilled copy are one notification.
  const data = msg.data as { notification_id?: string; task?: { id?: number } } | undefined
  const stable = data?.notification_id
    ?? (msg.event === 'task_update' && data?.task?.id != null ? `task:${data.task.id}` : undefined)
  const tagged = { ...base, event: msg.event }
  return stable ? { ...tagged, id: stable } : tagged
}

function toNotificationBody(msg: WsMessage, t: TFunc): AppNotification | null {
  switch (msg.event) {
    case 'order_created': {
      const ref = (msg.data as { reference?: string })?.reference
      return {
        id: rid(),
        type: 'order',
        title: t('notifications.orderCreated', 'New order received'),
        body: ref ? `#${ref}` : undefined,
        href: '/management/orders',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'task_update': {
      // Only failures are notification-worthy; in-progress/success is already
      // covered by the signage Task Center panel. The server broadcasts the
      // failed edge exactly once (services.tasks.sweep_failed_publishes), so
      // this won't re-fire on every recompute.
      const task = (msg.data as { task?: { status?: string; type?: string; target_label?: string } })?.task
      if (!task || task.status !== 'failed') return null
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.taskFailed', 'A publish did not reach every screen'),
        // Prefer the content name over the bare task type.
        body: task.target_label || task.type?.replace(/_/g, ' '),
        href: '/activity',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'screen_offline_alert': {
      // A screen has been offline past its configured alert threshold — pushed
      // once per offline streak by the attention sweep (maintenance.py).
      const data = msg.data as { screen_name?: string; minutes?: number }
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.screenOffline', 'A screen went offline'),
        body: data?.screen_name,
        href: '/screens',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'screen_license_expired': {
      // A screen's license lapsed and it was auto-unpaired — pushed once by the
      // attention sweep when it flips the row to is_paired=false.
      const data = msg.data as { screen_name?: string }
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.screenLicenseExpired', 'A screen licence expired'),
        body: data?.screen_name,
        href: '/screens',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'screen_license_expiring': {
      // A screen's license is approaching expiry — pushed once at 48h and once
      // at 24h before expiry by the attention sweep (maintenance.py).
      const data = msg.data as { screen_name?: string; hours?: number }
      const hours = data?.hours ?? 24
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.screenLicenseExpiring', {
          defaultValue: 'A screen licence expires in {{hours}}h',
          hours,
        }),
        body: data?.screen_name,
        href: '/screens',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'screen_license_renewed': {
      // A screen's license was renewed/extended by an admin — the seat's expiry
      // moved forward and the player un-orphans.
      const data = msg.data as { screen_name?: string }
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.screenLicenseRenewed', 'A screen licence was renewed'),
        body: data?.screen_name,
        href: '/screens',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'user_joined': {
      // A new teammate landed in the org — admin-created (create_user),
      // invite acceptance, or self-serve signup all fire this the same way.
      const data = msg.data as { name?: string; email?: string }
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.userJoined', 'A new teammate joined'),
        body: data?.name || data?.email,
        href: '/management/users',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'content_update': {
      // content_update is heavily overloaded — every ordinary publish
      // (screens/menus/playlists/media/...) rebroadcasts it to the org's own
      // dashboards too (see api/websocket.py's broadcast_to_screen, which also
      // reaches "that screen's org dashboards"), and that flavor carries a
      // screen_id, not a reason. Only the org-lifecycle flavor (admin
      // suspend/reactivate/archive/restore, see admin/routes/orgs.py) carries
      // `reason` — that's the ONLY content_update variant worth a bell entry;
      // every other one is silently ignored here on purpose (too frequent,
      // and it's the user's own action being echoed back to them).
      const reason = (msg.data as { reason?: string })?.reason
      if (!reason) return null
      const known: Record<string, string> = {
        org_suspended:   t('notifications.orgSuspended', 'Your organization was suspended'),
        org_reactivated: t('notifications.orgReactivated', 'Your organization was reactivated'),
        org_archived:    t('notifications.orgArchived', 'Your organization was archived'),
        org_restored:    t('notifications.orgRestored', 'Your organization was restored'),
      }
      if (!(reason in known)) return null
      return {
        id: rid(),
        type: 'system',
        title: known[reason],
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    case 'screen_license_issued': {
      // New licence seats were granted (issued) to the org by an admin.
      const data = msg.data as { count?: number }
      const count = data?.count ?? 1
      return {
        id: rid(),
        type: 'system',
        title: t('notifications.screenLicenseIssued', {
          defaultValue: '{{count}} new licence(s) issued',
          count,
        }),
        href: '/subscription',
        timestamp: new Date().toISOString(),
        read: false,
      }
    }
    default:
      return null
  }
}

/** Subscribes to the realtime hub only while mounted (i.e. only when authed). */
function NotificationWsBridge({ onMessage }: { onMessage: (msg: WsMessage) => void }) {
  useWebSocket(onMessage)
  return null
}

export function NotificationProvider({ children, app }: {
  children: ReactNode
  /** The app this bell is in — see APP_EVENTS. */
  app?: string
}) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const hiddenRef = useRef<Hidden>({ ids: [] })
  const lastBackfillRef = useRef(0)

  // Seed from cache on login; clear on logout so a different account never sees
  // another user's notifications.
  useEffect(() => {
    if (user) {
      setNotifications(loadStored(user.id).filter(n => belongsTo(app, n)))
      hiddenRef.current = loadHidden(user.id)
    } else {
      setNotifications([])
      hiddenRef.current = { ids: [] }
    }
    lastBackfillRef.current = 0
  }, [user, app])

  // Backfill from the server: alerts raised while no dashboard was open never
  // reached a socket (Frame QA A9-2). Runs on login and whenever the tab comes
  // back into view, throttled; merged by stable id, minus anything dismissed.
  const userId = user?.id
  const backfill = useCallback(async () => {
    if (userId == null) return
    if (Date.now() - lastBackfillRef.current < BACKFILL_MIN_INTERVAL_MS) return
    lastBackfillRef.current = Date.now()
    let events: BellEvent[]
    try {
      const { clearedAt } = hiddenRef.current
      events = (await getNotifications(clearedAt ? { since: clearedAt } : undefined)).data
    } catch {
      return // best-effort; the live socket still works
    }
    const hidden = new Set(hiddenRef.current.ids)
    setNotifications(prev => {
      const have = new Set(prev.map(n => n.id))
      const add: AppNotification[] = []
      for (const e of events) {
        if (have.has(e.id) || hidden.has(e.id)) continue
        const n = toNotification(bellEventToMessage(e), t)
        if (!n || !belongsTo(app, n)) continue
        const at = new Date(e.created_at).getTime()
        const dup = prev.some(p => p.title === n.title && p.body === n.body
          && Math.abs(new Date(p.timestamp).getTime() - at) < SAME_EVENT_WINDOW_MS)
        if (dup) continue
        add.push({ ...n, id: e.id, timestamp: e.created_at })
      }
      if (!add.length) return prev
      return [...add, ...prev]
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        .slice(0, MAX_ITEMS)
    })
  }, [userId, t, app])

  useEffect(() => {
    if (userId == null) return
    void backfill()
    const onVisible = () => { if (document.visibilityState === 'visible') void backfill() }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [userId, backfill])

  // Persist on every change (per-user key). Best-effort — storage may be full
  // or unavailable in private modes.
  useEffect(() => {
    if (!user) return
    try {
      localStorage.setItem(storageKey(user.id), JSON.stringify(notifications))
    } catch {
      /* ignore */
    }
  }, [user, notifications])

  const onMessage = useCallback((msg: WsMessage) => {
    const next = toNotification(msg, t)
    if (!next || !belongsTo(app, next)) return
    setNotifications(prev => [next, ...prev].slice(0, MAX_ITEMS))
  }, [t, app])

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => (n.read ? n : { ...n, read: true })))
  }, [])

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)))
  }, [])

  const clearAll = useCallback(() => {
    setNotifications([])
    if (userId == null) return
    hiddenRef.current = { clearedAt: new Date().toISOString(), ids: [] }
    saveHidden(userId, hiddenRef.current)
  }, [userId])

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    if (userId == null) return
    hiddenRef.current = { ...hiddenRef.current, ids: [...hiddenRef.current.ids, id] }
    saveHidden(userId, hiddenRef.current)
  }, [userId])

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications])

  const value = useMemo<NotificationValue>(
    () => ({ notifications, unreadCount, markAllRead, markRead, clearAll, dismiss }),
    [notifications, unreadCount, markAllRead, markRead, clearAll, dismiss],
  )

  return (
    <NotificationContext.Provider value={value}>
      {user && <NotificationWsBridge onMessage={onMessage} />}
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications(): NotificationValue {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider')
  return ctx
}

/** Compact relative time ("now", "5m", "3h", "2d") localized for the active language. */
export function formatRelativeTime(iso: string, lang: string): string {
  const then = new Date(iso).getTime()
  const diffSec = Math.round((then - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'narrow' })
  if (abs < 60) return rtf.format(Math.trunc(diffSec), 'second')
  if (abs < 3600) return rtf.format(Math.trunc(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.trunc(diffSec / 3600), 'hour')
  return rtf.format(Math.trunc(diffSec / 86400), 'day')
}
